/**
 * What the apps call. Two implementations:
 * - `RemoteAiService`: our backend (services/api), which holds the API key and calls the model;
 * - `DemoAiService`: no network — returns a fixed example board and uses the local text parser.
 *   The UI tells the user when it is in demo mode.
 */
import { describedCircuitsSchema, parseCircuitText, type DescribedCircuit } from './circuits.ts';
import type { ImageMediaType } from './extract.ts';
import { boardExtractionSchema, type BoardExtraction } from './schema.ts';
import {
  assistResponseSchema,
  parseAssistRequest,
  type AssistMessage,
  type AssistResponse,
} from './assist.ts';
import { DEMO_SKETCH, sketchSchema, type Sketch } from './sketch.ts';
import { DEMO_PART, partSchema, type Part } from './part.ts';
import {
  DEMO_REPORT,
  inspectionReportSchema,
  MAX_REPORT_PAGES,
  type InspectionReport,
} from './inspection.ts';
import type { Project } from '@elec/domain';
import { z } from 'zod';
import { projectContext } from './assist.ts';
import type { AssistFeedback, FeedbackConsent, SharedAnswer } from './feedback.ts';

export interface AiService {
  readonly mode: 'demo' | 'remote';
  extractBoard(image: Uint8Array, mediaType: ImageMediaType): Promise<BoardExtraction>;
  describeCircuits(text: string, locale: 'fr' | 'en' | 'nl'): Promise<DescribedCircuit[]>;
  /** Chat assistant: the conversation so far (last message = the request) → reply + actions. */
  /** `locale`: the interface language, for the findings' explanations in the context. */
  /** `onReply`: the reply as it is written (streamed), before the actions arrive. */
  assist(
    messages: AssistMessage[],
    project: Project,
    locale?: 'fr' | 'en' | 'nl',
    onReply?: (reply: string) => void,
  ): Promise<AssistResponse>;
  /** Hand sketch of a single-line diagram → board structure. */
  readSketch(image: Uint8Array, mediaType: ImageMediaType): Promise<Sketch>;
  /** One device's or cable's markings from a close-up photo. */
  readPart(image: Uint8Array, mediaType: ImageMediaType): Promise<Part>;
  /** Pages of an approved body's inspection report (JPEG) → its header and remarks, to review. */
  readInspectionReport(
    pages: Array<{ bytes: Uint8Array; mediaType: ImageMediaType }>,
  ): Promise<InspectionReport>;
  /** The server can turn speech into text (dictation in the assistant). */
  readonly canTranscribe: boolean;
  /** 16 kHz mono WAV → text. `locale` is a hint for the language spoken. */
  transcribe(wav: Uint8Array, locale: 'fr' | 'en' | 'nl'): Promise<string>;
  /** 👍/👎 on an answer (see feedback.ts); resolves to its id. The demo keeps nothing. */
  sendFeedback(feedback: AssistFeedback): Promise<string>;
  /** Share an answer already rated (after a 👍). */
  shareFeedback(id: string, shared: SharedAnswer, consent: FeedbackConsent): Promise<void>;
}

const toBase64 = (bytes: Uint8Array) => {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

export class AiServiceError extends Error {
  /** HTTP status of the server's answer (read from "502 …" messages); 0 when the server could
   * not be reached; -1 for other errors. */
  readonly status: number;
  constructor(message: string, status?: number) {
    super(message);
    this.status = status ?? (Number(message.match(/^(\d{3}) /)?.[1]) || -1);
  }
  /**
   * The AI cannot be reached right now: no network, the Tablo server down, or its model server
   * switched off (503 "AI temporarily unavailable", 502, 504). The apps say so plainly instead of
   * showing an error; the rest of Tablo keeps working.
   */
  get unavailable(): boolean {
    if (this.status === 503) return !/not configured/.test(this.message);
    return this.status === 0 || this.status === 502 || this.status === 504;
  }
}

/** `fetch` that turns "no network / server unreachable" into an AiServiceError (status 0). */
const reach = async (url: string, init: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (e) {
    throw new AiServiceError(`network: ${(e as Error).message}`, 0);
  }
};

/** Whether an error thrown by an AiService means "the AI is temporarily unavailable". */
export const isAiUnavailable = (e: unknown): boolean => e instanceof AiServiceError && e.unavailable;

export class RemoteAiService implements AiService {
  readonly mode = 'remote' as const;
  readonly canTranscribe: boolean;
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  /**
   * `getToken`: the signed-in user's session (the server only serves the AI to accounts).
   * `transcribe`: the server announced dictation in /api/health.
   */
  constructor(
    baseUrl: string,
    getToken: () => string | null = () => null,
    opts: { transcribe?: boolean } = {},
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.getToken = getToken;
    this.canTranscribe = !!opts.transcribe;
  }
  private async post<T>(path: string, body: unknown, parse: (v: unknown) => T): Promise<T> {
    const res = await reach(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(this.getToken() ? { authorization: `Bearer ${this.getToken()}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok)
      throw new AiServiceError(`${res.status} ${(await res.text().catch(() => '')).slice(0, 200)}`);
    return parse(await res.json());
  }
  extractBoard(image: Uint8Array, mediaType: ImageMediaType) {
    return this.post('/api/ai/board', { image: toBase64(image), mediaType }, (v) =>
      boardExtractionSchema.parse(v),
    );
  }
  describeCircuits(text: string, locale: 'fr' | 'en' | 'nl') {
    return this.post('/api/ai/circuits', { text, locale }, (v) => describedCircuitsSchema.parse(v).circuits);
  }
  async assist(
    messages: AssistMessage[],
    project: Project,
    locale?: 'fr' | 'en' | 'nl',
    onReply?: (reply: string) => void,
  ): Promise<AssistResponse> {
    const plain = messages.map(({ role, text }) => ({ role, text }));
    const body = {
      messages: plain,
      context: projectContext(project, locale),
      standard: project.meta.standard ?? 'rgie',
      ...(onReply ? { stream: true } : {}),
    };
    if (!onReply) return this.post('/api/ai/assist', body, (v) => assistResponseSchema.parse(v));
    const res = await reach(`${this.baseUrl}/api/ai/assist`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(this.getToken() ? { authorization: `Bearer ${this.getToken()}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok)
      throw new AiServiceError(`${res.status} ${(await res.text().catch(() => '')).slice(0, 200)}`);
    // An older server, or one that answered before streaming: plain JSON.
    if (!(res.headers.get('content-type') ?? '').includes('ndjson'))
      return assistResponseSchema.parse(await res.json());
    return readAssistStream(res, onReply);
  }
  readSketch(image: Uint8Array, mediaType: ImageMediaType) {
    return this.post('/api/ai/sketch', { image: toBase64(image), mediaType }, (v) => sketchSchema.parse(v));
  }
  readPart(image: Uint8Array, mediaType: ImageMediaType) {
    return this.post('/api/ai/part', { image: toBase64(image), mediaType }, (v) => partSchema.parse(v));
  }
  readInspectionReport(pages: Array<{ bytes: Uint8Array; mediaType: ImageMediaType }>) {
    return this.post(
      '/api/ai/inspection',
      {
        pages: pages
          .slice(0, MAX_REPORT_PAGES)
          .map((p) => ({ image: toBase64(p.bytes), mediaType: p.mediaType })),
      },
      (v) => inspectionReportSchema.parse(v),
    );
  }
  transcribe(wav: Uint8Array, locale: 'fr' | 'en' | 'nl') {
    return this.post(
      '/api/ai/transcribe',
      { audio: toBase64(wav), locale },
      (v) => transcriptSchema.parse(v).text,
    );
  }
  sendFeedback(feedback: AssistFeedback) {
    return this.post('/api/ai/feedback', feedback, (v) => feedbackIdSchema.parse(v).id);
  }
  async shareFeedback(id: string, shared: SharedAnswer, consent: FeedbackConsent) {
    await this.post('/api/ai/feedback/share', { id, shared, consent }, () => undefined);
  }
}

const transcriptSchema = z.object({ text: z.string() });
const feedbackIdSchema = z.object({ id: z.string() });

/** A plausible Belgian board: main switch, 300 mA RCD with 4 circuits, 30 mA RCD with 3. */
export const DEMO_EXTRACTION: BoardExtraction = {
  devices: [
    {
      row: 1,
      index: 1,
      deviceType: 'main-switch',
      ratingA: 40,
      curve: null,
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: null,
      confidence: 0.95,
    },
    {
      row: 1,
      index: 2,
      deviceType: 'RCD',
      ratingA: 40,
      curve: null,
      poles: 2,
      sensitivityMa: 300,
      rcdType: 'A',
      label: null,
      confidence: 0.93,
    },
    {
      row: 1,
      index: 3,
      deviceType: 'MCB',
      ratingA: 20,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Prises séjour',
      confidence: 0.92,
    },
    {
      row: 1,
      index: 4,
      deviceType: 'MCB',
      ratingA: 16,
      curve: 'B',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Lumières',
      confidence: 0.9,
    },
    {
      row: 1,
      index: 5,
      deviceType: 'MCB',
      ratingA: 20,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Chauffe-eau',
      confidence: 0.74,
    },
    {
      row: 1,
      index: 6,
      deviceType: 'MCB',
      ratingA: 32,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Taque',
      confidence: 0.88,
    },
    {
      row: 2,
      index: 1,
      deviceType: 'RCD',
      ratingA: 40,
      curve: null,
      poles: 2,
      sensitivityMa: 30,
      rcdType: 'A',
      label: null,
      confidence: 0.91,
    },
    {
      row: 2,
      index: 2,
      deviceType: 'MCB',
      ratingA: 20,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Salle de bain',
      confidence: 0.89,
    },
    {
      row: 2,
      index: 3,
      deviceType: 'MCB',
      ratingA: 20,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: 'Lave-linge',
      confidence: 0.86,
    },
    {
      row: 2,
      index: 4,
      deviceType: 'MCB',
      ratingA: null,
      curve: 'C',
      poles: 2,
      sensitivityMa: null,
      rcdType: null,
      label: null,
      confidence: 0.41,
    },
  ],
  notes:
    'Démo : résultat d’exemple, la photo n’a pas été analysée. / Demo: sample result, the photo was not analysed.',
};

export class DemoAiService implements AiService {
  readonly mode = 'demo' as const;
  /** Dictation needs a speech server: no microphone in demo mode. */
  readonly canTranscribe = false;
  private readonly delayMs: number;
  constructor(delayMs = 900) {
    this.delayMs = delayMs;
  }
  async extractBoard(): Promise<BoardExtraction> {
    await new Promise((r) => setTimeout(r, this.delayMs));
    return structuredClone(DEMO_EXTRACTION);
  }
  async describeCircuits(text: string): Promise<DescribedCircuit[]> {
    return parseCircuitText(text);
  }
  async assist(messages: AssistMessage[], project: Project): Promise<AssistResponse> {
    await new Promise((r) => setTimeout(r, Math.min(this.delayMs, 300)));
    return parseAssistRequest(messages.at(-1)?.text ?? '', project);
  }
  async readSketch(): Promise<Sketch> {
    await new Promise((r) => setTimeout(r, this.delayMs));
    return structuredClone(DEMO_SKETCH);
  }
  async readPart(): Promise<Part> {
    await new Promise((r) => setTimeout(r, Math.min(this.delayMs, 300)));
    return structuredClone(DEMO_PART);
  }
  async readInspectionReport(): Promise<InspectionReport> {
    await new Promise((r) => setTimeout(r, this.delayMs));
    return structuredClone(DEMO_REPORT);
  }
  async transcribe(): Promise<string> {
    throw new AiServiceError('dictation is not available in demo mode');
  }
  async sendFeedback(): Promise<string> {
    return '';
  }
  async shareFeedback(): Promise<void> {}
}

/** Remote service when an API URL is configured, demo otherwise. */
/** NDJSON from /api/ai/assist: {"reply"} lines as it is written, then {"done"} or {"error"}. */
export async function readAssistStream(
  res: Response,
  onReply: (reply: string) => void,
): Promise<AssistResponse> {
  const reader = res.body!.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (value) buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      const v = JSON.parse(line) as { reply?: string; done?: unknown; error?: string };
      if (v.error) throw new AiServiceError(`502 ${v.error}`);
      if (v.done) return assistResponseSchema.parse(v.done);
      if (typeof v.reply === 'string') onReply(v.reply);
    }
    if (done) throw new AiServiceError('502 the answer was cut off');
  }
}

export function createAiService(
  apiUrl: string | undefined,
  getToken?: () => string | null,
  opts: { transcribe?: boolean } = {},
): AiService {
  return apiUrl ? new RemoteAiService(apiUrl, getToken, opts) : new DemoAiService();
}
